import { Truck, Search, Package, MapPin, FileDown, Upload, Undo2, CheckCircle2 } from 'lucide-react';
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../utils/api';
import { AuthContext } from '../contexts/AuthContext';
import { normalizeRole } from '../utils/roleUtils';
import { formatRecordDate } from '../utils/dateUtils';
import toast from 'react-hot-toast';

const AUTO_REFRESH_INTERVAL_MS = 30000;

const ReadyToShip = () => {
    const { user } = useContext(AuthContext);
    const normalizedRole = normalizeRole(user?.role);
    const canShip = normalizedRole === 'admin' || normalizedRole === 'shipment_staff';
    const isAdmin = normalizedRole === 'admin';

    const [readyItems, setReadyItems] = useState([]);
    const [shippedItems, setShippedItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const fileInputRef = useRef(null);
    const proofTargetIdRef = useRef(null);

    const fetchLists = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setIsLoading(true);
        try {
            const [readyRes, shippedRes] = await Promise.all([
                api.get('/shipments', { params: { status: 'ready', search: searchTerm || undefined, limit: 100 } }),
                api.get('/shipments', { params: { status: 'shipped', limit: 10 } }),
            ]);
            setReadyItems(readyRes.data.data || []);
            setShippedItems(shippedRes.data.data || []);
        } catch (error) {
            console.error('Sevke hazır liste alınamadı', error);
            toast.error('Liste yüklenemedi.');
        } finally {
            if (!silent) setIsLoading(false);
        }
    }, [searchTerm]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchLists();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [fetchLists]);

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            fetchLists({ silent: true });
        }, AUTO_REFRESH_INTERVAL_MS);

        return () => window.clearInterval(intervalId);
    }, [fetchLists]);

    const promptProofUpload = (shipmentId) => {
        proofTargetIdRef.current = shipmentId;
        fileInputRef.current?.click();
    };

    const handleProofFileSelected = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        const shipmentId = proofTargetIdRef.current;
        if (!file || !shipmentId) return;

        const formData = new FormData();
        formData.append('file', file);
        try {
            await api.post(`/shipments/${shipmentId}/proof`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Sevk kanıtı yüklendi.');
            fetchLists();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Kanıt yüklenemedi.');
        }
    };

    const handleMarkShipped = async (row) => {
        if (!canShip) {
            toast.error('Bu işlem için yetkiniz bulunmuyor.');
            return;
        }
        if (!window.confirm(`"${row.seller} → ${row.buyer}" (${row.sackCount} çuval) sevk edildi olarak işaretlensin mi?`)) return;

        setBusyId(row.id);
        try {
            const res = await api.patch(`/shipments/${row.id}/status`, { isShipped: 1 });
            toast.success('Sevk edildi olarak işaretlendi.');
            setReadyItems(prev => prev.filter(s => s.id !== row.id));
            setShippedItems(prev => [res.data, ...prev].slice(0, 10));
            if (window.confirm('Yüklü araç fotoğrafı eklemek ister misiniz? (Opsiyonel)')) {
                promptProofUpload(row.id);
            }
        } catch (error) {
            const message = error.response?.data?.message;
            if (error.response?.status === 409) { toast.error(message || 'Kayıt değişti, liste yenileniyor.'); fetchLists(); }
            else toast.error(message || 'Sevk işaretlenemedi.');
        } finally {
            setBusyId(null);
        }
    };

    const handleUnship = async (row) => {
        if (!isAdmin) return;
        if (!window.confirm('Sevk işareti geri alınsın mı? Kayıt tekrar "Sevke Hazır" listesine dönecek.')) return;

        setBusyId(row.id);
        try {
            await api.patch(`/shipments/${row.id}/status`, { isShipped: 0 });
            toast.success('Sevk işareti geri alındı.');
            fetchLists();
        } catch (error) {
            toast.error(error.response?.data?.message || 'İşlem başarısız.');
        } finally {
            setBusyId(null);
        }
    };

    const handleDownloadProof = (id) => {
        window.open(`${api.defaults.baseURL}/shipments/${id}/proof/download`, '_blank');
    };

    return (
        <Layout>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-xl md:text-3xl font-bold text-dark-text mb-2 tracking-tight">Sevke Hazır</h1>
                    <p className="text-dark-muted text-sm md:text-base">Faturası kesilmiş veya faturasız gidecek olarak işaretlenmiş, araca yüklenmeyi bekleyen sevkiyatlar.</p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 font-bold">
                    <Truck size={20} />
                    <span>{readyItems.length} kayıt bekliyor</span>
                </div>
            </div>

            {/* ARAMA */}
            <div className="glass p-4 rounded-xl mb-6 border border-slate-700/50">
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                        <Search size={18} />
                    </div>
                    <input
                        type="text"
                        placeholder="Satıcı, Alıcı, Şehir veya Gönderim Yolu Ara..."
                        className="w-full pl-11 pr-4 py-3 bg-slate-800/80 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* SEVKE HAZIR LİSTESİ */}
            <div className="space-y-3 mb-10">
                {isLoading ? (
                    <div className="py-16 flex justify-center">
                        <div className="w-8 h-8 rounded-full border-t-2 border-r-2 border-primary-500 animate-spin"></div>
                    </div>
                ) : readyItems.length > 0 ? (
                    readyItems.map((row) => (
                        <div key={row.id} className="glass rounded-xl border border-dark-border p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <span className="font-bold text-dark-text">{row.seller}</span>
                                    <span className="text-dark-muted">→</span>
                                    <span className="font-medium text-dark-text/90">{row.buyer}</span>
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                        <Package size={12} />
                                        {row.sackCount} çuval
                                    </span>
                                </div>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-dark-muted">
                                    <span className="inline-flex items-center gap-1"><MapPin size={12} /> {row.city}</span>
                                    <span>{row.route}</span>
                                    <span>Ekleyen: {row.createdBy || 'Bilinmiyor'}</span>
                                    {row.isInvoiceless === 1 ? (
                                        <span className="text-violet-400 font-semibold">FATURASIZ · {row.invoicelessBy} · {formatRecordDate(row.invoicelessAt)}</span>
                                    ) : (
                                        <span className="text-amber-400">Fatura: {row.invoicedBy} · {formatRecordDate(row.invoicedAt)}</span>
                                    )}
                                </div>
                            </div>
                            {canShip && (
                                <button
                                    onClick={() => handleMarkShipped(row)}
                                    disabled={busyId === row.id}
                                    className="w-full md:w-auto flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white px-6 py-3 rounded-xl font-bold tracking-wide transition-all shadow-lg shadow-green-500/20 hover:scale-105 active:scale-95"
                                >
                                    <Truck size={18} />
                                    <span>SEVK EDİLDİ</span>
                                </button>
                            )}
                        </div>
                    ))
                ) : (
                    <div className="glass rounded-xl border border-dark-border px-6 py-16 text-center text-dark-muted">
                        <Truck size={48} className="mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium">Sevke hazır kayıt yok.</p>
                        <p className="text-sm mt-1">Admin faturası kesilen sevkiyatı işaretlediğinde burada görünür.</p>
                    </div>
                )}
            </div>

            {/* SON SEVK EDİLENLER */}
            <div className="mb-4">
                <h2 className="text-lg font-bold text-dark-text mb-3 flex items-center gap-2">
                    <CheckCircle2 size={18} className="text-green-400" />
                    Son Sevk Edilenler
                </h2>
                <div className="space-y-2">
                    {shippedItems.length > 0 ? (
                        shippedItems.map((row) => (
                            <div key={row.id} className="glass rounded-xl border border-dark-border p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 text-sm">
                                        <span className="font-semibold text-dark-text">{row.seller}</span>
                                        <span className="text-dark-muted">→</span>
                                        <span className="text-dark-text/90">{row.buyer}</span>
                                        <span className="text-xs text-dark-muted">({row.sackCount} çuval, {row.city})</span>
                                    </div>
                                    <div className="text-xs text-green-400 mt-1">
                                        Sevk eden: {row.shippedBy || 'Bilinmiyor'} · {formatRecordDate(row.shippedAt)}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {row.shipProofPath ? (
                                        <button
                                            onClick={() => handleDownloadProof(row.id)}
                                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-green-400 bg-green-500/10 border border-green-500/20 hover:bg-green-500/20 transition-colors"
                                            title="Sevk kanıtını indir"
                                        >
                                            <FileDown size={14} />
                                            Kanıt
                                        </button>
                                    ) : canShip && (
                                        <button
                                            onClick={() => promptProofUpload(row.id)}
                                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-dark-muted bg-dark-hover border border-dark-border hover:text-dark-text transition-colors"
                                            title="Araç fotoğrafı ekle"
                                        >
                                            <Upload size={14} />
                                            Fotoğraf Ekle
                                        </button>
                                    )}
                                    {isAdmin && (
                                        <button
                                            onClick={() => handleUnship(row)}
                                            disabled={busyId === row.id}
                                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-red-400 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 disabled:opacity-50 transition-colors"
                                            title="Sevk işaretini geri al"
                                        >
                                            <Undo2 size={14} />
                                            Geri Al
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-dark-muted px-1">Henüz sevk edilen kayıt yok.</p>
                    )}
                </div>
            </div>

            {/* Gizli dosya girişi (sevk kanıtı) */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,application/pdf"
                className="hidden"
                onChange={handleProofFileSelected}
            />
        </Layout>
    );
};

export default ReadyToShip;
